/**
 * Permission keys — used by API mapping, `RequirePermission`, and `can()`.
 * List/tab access: `*.view` gates the whole route (table + dashboard on that section).
 */
export const PERMISSION_KEYS = {
  /** Main dashboard route (`/dashboard`) — from API `DASHBOARD_VIEW` */
  DASHBOARD_VIEW: "dashboard.view",

  BILLING_VIEW: "billing.view",

  /** SaaS billing — `/billing/saas-billing` — API `SAAS_BILLING_VIEW` */
  SAAS_BILLING_VIEW: "saasBilling.view",
  /** CSV/XLSX export & download — API `SAAS_BILLING_EXPORT` */
  SAAS_BILLING_EXPORT: "saasBilling.export",

  /** QuickBooks exports — `/billing/exports` — API `QB_BILLING_VIEW` */
  QB_BILLING_VIEW: "qbBilling.view",
  /** CSV/XLSX export & download on QB export preview — API `QB_BILLING_EXPORT` */
  QB_BILLING_EXPORT: "qbBilling.export",

  /** Month locking & audit — `/reports` */
  REPORTS_LOCK_AUDIT_VIEW: "reports.lockAudit.view",
  MONTH_LOCK_MANAGE: "reports.month.lock",
  MONTH_LOCK_UNLOCK: "reports.month.unlock",
  REPORTS_AUDIT_EXPORT: "reports.audit.export",

  SETTINGS_ROLE_PERMISSION: "settings.rolePermission",
  /** Client + vendor tag mapping settings (both tabs) */
  TAGS_VIEW: "tags.view",

  /** Client/vendor tags on `/settings/system-lists`: POST/PUT/DELETE `/system-lists` only. The page itself is not route-gated by these keys. */
  SYSTEM_LIST_ADD: "systemLists.add",
  SYSTEM_LIST_EDIT: "systemLists.edit",
  SYSTEM_LIST_DELETE: "systemLists.delete",

  /** Settings → System lists → SaaS module tab (master modules API). */
  SAAS_MODULE_ADD: "saasModule.add",
  SAAS_MODULE_EDIT: "saasModule.edit",
  SAAS_MODULE_DELETE: "saasModule.delete",

  PROJECTS_SETTINGS_MANAGE: "projects.settingsManage",

  USERS_VIEW: "users.view",
  USERS_CREATE: "users.create",
  USERS_EDIT: "users.edit",
  USERS_DELETE: "users.delete",

  /** List / route access — API `CLIENT_VIEW` and `CLIENT_VIEW_ALL` (see {@link CLIENTS_SIDEBAR}). */
  CLIENT_VIEW: "clients.view",
  /**
   * Sidebar “Clients” link — API `CLIENT_VIEW` only (not `CLIENT_VIEW_ALL`).
   * Users with all-clients scope still get {@link CLIENT_VIEW} for lists but no nav entry.
   */
  CLIENTS_SIDEBAR: "clients.sidebar",
  /** Per-client dashboard (`/clients?view=dashboard&clientId=…`) — API e.g. `CLIENT_DASHBOARD_VIEW` */
  CLIENTS_DASHBOARD_VIEW: "clients.dashboardView",
  CLIENTS_CREATE: "clients.create",
  CLIENTS_EDIT: "clients.edit",
  CLIENTS_DELETE: "clients.delete",

  TEAMS_VIEW: "teams.view",
  TEAMS_CREATE: "teams.create",
  TEAMS_EDIT: "teams.edit",
  TEAMS_DELETE: "teams.delete",
  TEAMS_ADD_MEMBER: "teams.addMember",

  /** List / route access — API `PROJECT_VIEW` and `PROJECT_VIEW_ALL`. */
  PROJECTS_VIEW: "projects.view",
  /** Sidebar “Projects” link — API `PROJECT_VIEW` only (not `PROJECT_VIEW_ALL`). */
  PROJECTS_SIDEBAR: "projects.sidebar",
  /** Per-project dashboard (`/projects/:slug?tab=dashboard`) — API `PROJECT_DASHBOARD_VIEW` or `PROJECTS_DASHBOARD_VIEW`. */
  PROJECTS_DASHBOARD_VIEW: "projects.dashboardView",
  PROJECTS_CREATE: "projects.create",
  PROJECTS_EDIT: "projects.edit",
  PROJECTS_DELETE: "projects.delete",
  /** Admin-only billing notes on project (clientNotes / vendorNotes) */
  PROJECT_BILLING_NOTES: "projects.billingNotes",

  /** All work items list — `/issues?tab=all-issue` and project Work Items / Backlog — API `WORK_ITEMS_VIEW` */
  WORK_ITEMS_VIEW: "workItems.view",
  /** Global `/issues` Kanban tab + project Kanban board — API `KANBAN_VIEW` (not implied by `WORK_ITEMS_VIEW`). */
  KANBAN_VIEW: "workItems.kanbanView",
  /** My work items — `/issues?tab=my-issue` — API `MY_WORK_ITEMS_VIEW` */
  MY_WORK_ITEMS_VIEW: "workItems.myView",
  WORK_ITEMS_CREATE: "workItems.create",
  WORK_ITEMS_EDIT: "workItems.edit",
  WORK_ITEMS_DELETE: "workItems.delete",

  /**
   * Time entry — one view key per tab (lists). Route/sidebar: any of
   * {@link TIME_ENTRY_TAB_VIEW_KEYS}. Mutations apply everywhere time can be logged/edited.
   */
  TIME_ENTRY_TIMESHEET_VIEW: "timeEntry.timesheetView",
  /** All Time Entries tab — GET /time-logs list */
  ALL_TIME_ENTRY_TIMESHEET_VIEW: "timeEntry.allTimeEntryTimesheetView",
  TIME_ENTRY_TEAM_SUMMARY_VIEW: "timeEntry.teamSummaryView",
  TIME_ENTRY_PROJECT_SUMMARY_VIEW: "timeEntry.projectSummaryView",
  TIME_ENTRY_CREATE: "timeEntry.create",
  TIME_ENTRY_EDIT: "timeEntry.edit",
  TIME_ENTRY_DELETE: "timeEntry.delete",
};

/** Tab view keys — used by `RequirePermission` (any) and sidebar `permissionKeys`. */
export const TIME_ENTRY_TAB_VIEW_KEYS = Object.freeze([
  PERMISSION_KEYS.TIME_ENTRY_TIMESHEET_VIEW,
  PERMISSION_KEYS.ALL_TIME_ENTRY_TIMESHEET_VIEW,
  PERMISSION_KEYS.TIME_ENTRY_TEAM_SUMMARY_VIEW,
  PERMISSION_KEYS.TIME_ENTRY_PROJECT_SUMMARY_VIEW,
]);

/** `/issues` sidebar + route: show when the user has any of these (tabs are gated individually). */
export const WORK_ITEMS_SIDEBAR_ANY_KEYS = Object.freeze([
  PERMISSION_KEYS.WORK_ITEMS_VIEW,
  PERMISSION_KEYS.KANBAN_VIEW,
  PERMISSION_KEYS.MY_WORK_ITEMS_VIEW,
]);

/**
 * `/clients` index (list or dashboard query): allow scoped list (`CLIENTS_SIDEBAR` from API
 * `CLIENT_VIEW`) or client dashboard (`CLIENTS_DASHBOARD_VIEW`). Excludes `CLIENT_VIEW_ALL`-only
 * roles so the shell does not mount and call APIs that return 403.
 */
export const CLIENTS_ROUTE_INDEX_ANY_KEYS = Object.freeze([
  PERMISSION_KEYS.CLIENTS_SIDEBAR,
  PERMISSION_KEYS.CLIENTS_DASHBOARD_VIEW,
]);

/**
 * `/projects/:projectSlug` workspace: project list (`PROJECTS_VIEW`), project dashboard
 * (`PROJECTS_DASHBOARD_VIEW`), project **Settings** (`PROJECTS_SETTINGS_MANAGE`), or work-item
 * areas (work-item tabs: list/backlog vs Kanban vs dashboard/settings).
 */
export const PROJECTS_ROUTE_WORKSPACE_ANY_KEYS = Object.freeze([
  PERMISSION_KEYS.PROJECTS_VIEW,
  PERMISSION_KEYS.PROJECTS_DASHBOARD_VIEW,
  PERMISSION_KEYS.PROJECTS_SETTINGS_MANAGE,
  PERMISSION_KEYS.WORK_ITEMS_VIEW,
  PERMISSION_KEYS.KANBAN_VIEW,
  PERMISSION_KEYS.MY_WORK_ITEMS_VIEW,
]);

/**
 * Settings → System lists (`/settings/system-lists`): route guard and sidebar use “any of”
 * these keys so direct URL access matches menu visibility.
 */
export const SETTINGS_SYSTEM_LISTS_ANY_KEYS = Object.freeze([
  PERMISSION_KEYS.TAGS_VIEW,
  PERMISSION_KEYS.SYSTEM_LIST_ADD,
  PERMISSION_KEYS.SYSTEM_LIST_EDIT,
  PERMISSION_KEYS.SYSTEM_LIST_DELETE,
  PERMISSION_KEYS.SAAS_MODULE_ADD,
  PERMISSION_KEYS.SAAS_MODULE_EDIT,
  PERMISSION_KEYS.SAAS_MODULE_DELETE,
]);

export const ALL_PERMISSION_KEYS = Object.freeze(Object.values(PERMISSION_KEYS));
